"use client";

import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { GlobeIcon, ShieldCheckIcon, UsersIcon, ZapIcon } from "lucide-react";

import { Container } from "@/app/(marketing)/_components/ui/container";

interface Feature {
  icon: LucideIcon;
  title: string;
  description: string;
  highlights: string[];
}

const features: Feature[] = [
  {
    icon: UsersIcon,
    title: "AI-Native Expert Panel",
    description:
      "Access a pre-vetted network of verified experts across every industry and geography.",
    highlights: [
      "No third-party panel dependencies",
      "Continuously verified credentials",
      "195 countries, 200+ domains",
    ],
  },
  {
    icon: ZapIcon,
    title: "Instant Recruitment",
    description:
      "Go from audience definition to verified experts in hours, not weeks.",
    highlights: [
      "AI agents source and screen 24/7",
      "Natural language targeting",
      "Real-time qualification updates",
    ],
  },
  {
    icon: ShieldCheckIcon,
    title: "Verified & Fraud-Free",
    description:
      "Every expert is identity-verified and credential-checked before delivery.",
    highlights: [
      "Multi-factor identity verification",
      "LinkedIn and credential validation",
      "AI-powered fraud detection",
    ],
  },
  {
    icon: GlobeIcon,
    title: "Global Reach",
    description: "Recruit hard-to-reach professionals anywhere in the world.",
    highlights: [
      "Multi-language recruitment",
      "Local payment rails everywhere",
      "Time zone-aware scheduling",
    ],
  },
];

export default function MarketResearchFeatures() {
  return (
    <section className="py-20">
      <Container>
        {/* Section header */}
        <div className="mb-16 flex flex-col items-center gap-8">
          <div className="bg-marketing-card-blue rounded-t-[10px] px-2.5 py-1.5">
            <p className="text-marketing-blue font-mono text-sm tracking-[-0.28px]">
              Capabilities
            </p>
          </div>
          <h2 className="text-marketing-text dark:text-marketing-cream max-w-[700px] text-center text-3xl leading-[1.1] font-medium tracking-[-1.5px] md:text-4xl lg:text-[50px]">
            Panel infrastructure built for modern research
          </h2>
        </div>

        {/* Feature cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, transform: "translateY(20px)" }}
              whileInView={{ opacity: 1, transform: "translateY(0px)" }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="border-marketing-border bg-marketing-cream-light rounded-[20px] border p-8"
            >
              <div className="bg-marketing-card-blue mb-4 flex h-12 w-12 items-center justify-center rounded-full">
                <feature.icon className="text-marketing-blue h-6 w-6" />
              </div>
              <h3 className="text-marketing-text mb-3 text-xl font-medium">
                {feature.title}
              </h3>
              <p className="text-marketing-text-muted mb-6 text-base leading-relaxed">
                {feature.description}
              </p>
              <div className="flex flex-col gap-2">
                {feature.highlights.map((highlight) => (
                  <div key={highlight} className="flex items-center gap-2.5">
                    <div className="bg-marketing-blue h-1.5 w-1.5 shrink-0 rounded-full" />
                    <p className="text-marketing-text text-sm font-medium">
                      {highlight}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
}
